import { Alert, Box, Button, Typography } from "@mui/material";
import { AffectGrid } from "../questionnaireComponents/AffectGrid";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { DummyQuestion } from "../questionnaireComponents/DummyQuestion";
import { EventValueQuestion } from "../questionnaireComponents/EventValueQuestion";
import { EventControlQuestion } from "../questionnaireComponents/EventControlQuestion";

import { motion, AnimatePresence } from "framer-motion";
import { EventRelationQuestion } from "../questionnaireComponents/EventRelationQuestion";
import { useAuth } from "../../context/AuthContext";
import {
  defaultPanasAnswer,
  Panas,
  panasAnsewerType,
} from "../questionnaireComponents/Panas";

type affectValueType = {
  pleasantness: number;
  arousal: number;
};

let maxPageNumber = 0;

const variants = {
  enter: (dir: "left" | "right") => ({
    x: dir === "right" ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { x: "0%", opacity: 1 },
  exit: (dir: "left" | "right") => ({
    x: dir === "right" ? "-100%" : "100%",
    opacity: 0,
  }),
};

// 項目が変わらない場合はPOSTと共通化する
export const DebugPrePage = () => {
  const navigate = useNavigate();
  const { idToken, refreshAuthSession } = useAuth();
  const [affectValue, setAffectValue] = useState<affectValueType | null>(null);
  const [dummyValue, setDummyValue] = useState<number | null>(null);
  const [eventValue, setEventValue] = useState<number | null>(null);
  const [eventControllable, setEventControllable] = useState<boolean | null>(
    null
  );
  const [eventRelation, setEventRelation] = useState<boolean | null>(null);
  const [panasAnswer, setPanasAnswer] =
    useState<panasAnsewerType>(defaultPanasAnswer);

  const [includePanas, setIncludePanas] = useState<boolean | null>(null);

  const [questionnairePage, setQuestionnairePage] = useState<number>(1);

  const [answerComplete, setAnswerComplete] = useState<boolean>(false);

  const directionRef = useRef<"left" | "right">("right");

  const isAnswerEnable =
    affectValue &&
    eventValue &&
    eventControllable !== null &&
    eventRelation !== null &&
    (includePanas ? isAllNotNull(panasAnswer) : true);

  const alertMessageIsOn =
    (!includePanas && !isAnswerEnable && affectValue !== null) ||
    (includePanas && !isAnswerEnable && isAllNotNull(panasAnswer));

  // PANASの回答が完了しているかどうかを判定する関数
  function isAllNotNull(obj: {
    [key: string]: number | null;
  }): obj is { [key: string]: number } {
    return Object.values(obj).every((v): v is number => v !== null);
  }

  const answerButtonClicked = () => {
    // アンケートデータ保存処理
    if (!isAnswerEnable) return;
    if (!idToken) return;

    const data = {
      affectGrid: affectValue,
      eventValue: eventValue,
      eventControllable: eventControllable,
      eventRelation: eventRelation,
    };
    setAnswerComplete(true);
  };

  const switchTab = (next: number) => {
    const dir = next > questionnairePage ? "right" : "left";
    directionRef.current = dir;
    setQuestionnairePage(next);
  };

  // 最初の読み込み PANASの回答が済んでているかどうかでアンケート内容が変わる
  useEffect(() => {
    // if (idToken && state.userId) {
    //   getFirstDayAnswerById(state.userId, idToken).then((data) => {
    //     // データが無い場合はダミーデータ[]が返ってくる
    //     if (data.length === 0) {
    //       maxPageNumber = 5;
    //       setIncludePanas(true);
    //     } else {
    //       maxPageNumber = 4;
    //       setIncludePanas(false);
    //     }
    //   });
    // }
  }, []);

  const firstDayButton = () => {
    maxPageNumber = 5;
    setIncludePanas(true);
  };

  const secondDayButton = () => {
    maxPageNumber = 4;
    setIncludePanas(false);
  };

  if (answerComplete)
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          回答が完了しました。
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          この回答結果は保存されません。
        </Typography>

        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#f9f9f9",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            回答データ：
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Affect Grid：</strong>
            {affectValue
              ? `pleasant: ${affectValue.pleasantness}, arousal: ${affectValue.arousal}`
              : "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>出来事の価値：</strong> {eventValue ?? "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>出来事のコントロール：</strong>
            {eventControllable !== null
              ? eventControllable
                ? "はい"
                : "いいえ"
              : "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>他者との関連性：</strong>
            {eventRelation !== null
              ? eventRelation
                ? "はい"
                : "いいえ"
              : "未回答"}
          </Typography>

          {includePanas && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>PANAS：</strong>
              </Typography>
              <Box sx={{ ml: 2 }}>
                {Object.entries(panasAnswer).map(([key, value]) => (
                  <Typography key={key} variant="caption" display="block">
                    {key}: {value ?? "未回答"}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        <Button variant="outlined" onClick={() => navigate("/")}>
          戻る
        </Button>
        <div style={{ height: "2rem" }} />
      </Box>
    );

  if (includePanas === null)
    return (
      <Box
        sx={{
          textAlign: "center",
          marginBottom: "1rem",
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Button color="primary" onClick={firstDayButton}>
          初日アンケートを確認
        </Button>
        <Button color="primary" onClick={secondDayButton}>
          2日目以降のアンケートを確認
        </Button>
      </Box>
    );

  return (
    <>
      <Box sx={{ minHeight: "60%" }}>
        <Box sx={{ textAlign: "center", marginBottom: "1rem" }}>
          <Typography variant="h6">
            事前アンケート({questionnairePage}/{maxPageNumber})
          </Typography>
          <Typography variant="caption">
            チャット前のアンケートにご回答ください。
          </Typography>
        </Box>

        {/* <Button
          variant="contained"
          color="error"
          onClick={() => setQuestionnairePage(10)}
        >
          TEST
        </Button> */}

        <Box sx={pageButtonContainerStyle}>
          <Button
            onClick={() => switchTab(questionnairePage - 1)}
            disabled={questionnairePage === 1}
          >
            <ArrowBackIosIcon />
            前の設問へ
          </Button>
          <Button
            onClick={() => switchTab(questionnairePage + 1)}
            disabled={questionnairePage === maxPageNumber}
          >
            次の設問へ
            <ArrowForwardIosIcon />
          </Button>
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={questionnairePage}
            custom={directionRef.current}
            variants={variants}
            initial="enter"
            animate="center"
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              bounce: 0, // ← ここを追加
              duration: 0.5,
            }}
          >
            {questionnairePage === 1 && (
              <EventValueQuestion
                eventValue={eventValue}
                setEventValue={setEventValue}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}
            {questionnairePage === 2 && (
              <EventControlQuestion
                eventControllable={eventControllable}
                setEventControllable={setEventControllable}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}

            {questionnairePage === 3 && (
              <EventRelationQuestion
                eventRelation={eventRelation}
                setEventRelation={setEventRelation}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}

            {questionnairePage === 4 && (
              <AffectGrid
                affectValue={affectValue}
                setAffectValue={setAffectValue}
                isAutoPageTurn={includePanas}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
                widthValue={window.innerWidth}
              />
            )}

            {questionnairePage === 5 && (
              <Panas
                panasAnswer={panasAnswer}
                setPanasAnswer={setPanasAnswer}
              />
            )}

            {questionnairePage === 100 && (
              <DummyQuestion value={dummyValue} setValue={setDummyValue} />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      <Box sx={{ textAlign: "center", marginTop: "3rem" }}>
        <Button
          variant="contained"
          disabled={!isAnswerEnable}
          style={{ width: "fit-content" }}
          onClick={answerButtonClicked}
        >
          回答する
        </Button>
        {alertMessageIsOn && (
          <Typography sx={{ marginTop: "0.2rem", color: "#C0392B" }}>
            未回答の設問があります！
          </Typography>
        )}
        <div style={{ height: "1rem" }} />
      </Box>
    </>
  );
};

// css
const pageButtonContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "0.5rem 1rem",
};
